/**
 * Body rows for `DataViewTable`. Handles three layouts per row:
 *
 *  - Normal: single `<TableRow>` with one cell per visible column.
 *  - Split (inspector + `config.rowSplit`): two stacked rows representing
 *    Lightspeed vs Xero, with `spansSystems` cells merged via rowSpan=2.
 *  - Expanded detail: rendered as an additional `<TableRow>` below when
 *    `config.expandable` is set and the row is currently expanded.
 *
 * Per-row closures (`renderCellNode`, `cellSxFor`) are scoped inside the
 * map so each row reads the current `row` / `rowBaseBg` without prop
 * drilling.
 */
import React from 'react'
import {
  Button,
  Collapse,
  IconButton,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import BalanceIcon from '@mui/icons-material/Balance'
import DifferenceIcon from '@mui/icons-material/Difference'
import type {
  ExceptionColumnDef,
  ExceptionConfig,
  ExceptionContext,
  RenderContext,
} from '../../../config/exceptions/types'
import type { ColumnDef } from '../../../pages/admin/PlatformReports/reports/BaseReportPage'
import {
  formatCellValue,
  getColumnStyle,
  ROW_HIGHLIGHT_COLORS,
} from '../../../utils/tableFormatters'
import {
  ACTIVE_ROW_ACCENT,
  CLOSED_ROW_BG,
  CLOSED_ROW_OPACITY,
  GROUP_SEPARATOR_BORDER,
  INACTIVE_ROW_OPACITY,
  ZEBRA_BG,
} from '../../../theme/exceptionTableStyles'

/**
 * Selector matching elements whose own click handling should take
 * priority over a row-level expand toggle.
 */
const INTERACTIVE_TARGET_SELECTOR =
  'a, button, [role="button"], .MuiChip-root, .MuiTooltip-popper'

export interface TableBodyRowsProps {
  displayedRows: Record<string, any>[]
  visibleColumns: ExceptionColumnDef[]
  config: ExceptionConfig
  variant: ExceptionContext
  fpCode: string | undefined
  isInspector: boolean
  expandedRows: Record<string, boolean>
  onToggleExpand: (rowKey: string) => void
  rowClickEnabled: boolean
  openRowDetail: (row: Record<string, any>) => void
  hasExpandable: boolean
  hasRowDetail: boolean
  /**
   * Per-row Compare-button gate. compare-sales: the backend stamps
   * ``row.compare_sales`` only on eligible rows; duplicate-group: the row
   * must carry a detector group id. Ineligible rows keep the rowDetail
   * column (alignment) but render an empty cell instead of the button.
   */
  rowDetailEnabledFor?: (row: Record<string, any>) => boolean
  /** Which rowDetail dialog the button opens - picks the label + icon. */
  rowDetailType?: 'compare-sales' | 'duplicate-group'
  /**
   * When true (duplicate-group configs with ``rowClick: true``), clicking
   * anywhere on an eligible row opens the rowDetail dialog. Interactive
   * cells (links, chips, CountChip) keep their own behaviour via
   * ``INTERACTIVE_TARGET_SELECTOR``. Priority: expand > rowDetail > drawer.
   */
  rowDetailRowClickEnabled?: boolean
  isRowSplit: boolean
  /**
   * Pair of system identifiers controlling the order of the two stacked
   * rows in split mode. Mirrors `ExceptionConfig.rowSplit.systems` (the
   * source-of-truth array type) and is treated as a 2-tuple at use site.
   */
  rowSplitSystems: Array<'lightspeed' | 'xero'> | null
  totalColSpan: number
  groupSeparatorKeys: Set<string>
  getFreezeBodySx: (col: ExceptionColumnDef) => Record<string, any>
  tableRowDivider: string
  rowHoverBg: string
  /**
   * When true, every row renders with reduced opacity + a muted grey
   * background so the closed (resolved or ignored) view is visually
   * distinct from the default open view. Driven by ?status=closed on
   * the route URL.
   */
  isClosedView?: boolean
  /**
   * Stable row key of the row whose note side-panel is currently
   * open. The matching row gets a persistent pale teal tint via the
   * row-scoped --row-bg CSS variable so the user has a visual link
   * between the open thread and its originating row. Null when no
   * panel is open.
   */
  activeRowKey?: string | null
  /**
   * Pale teal applied to the active row's --row-bg variable. Lifted to
   * a prop so the table host (DataViewTable) owns colour choice
   * via its existing theme imports.
   */
  activeRowBg?: string
  /**
   * Open the per-row ``ExceptionDrawer`` for the given row. Wired by
   * ``DataViewTable`` to ``setDrawerRow``. The per-row eligibility
   * check (does this row carry the tuple identity) is evaluated here
   * so one malformed row doesn't disable the drawer for siblings.
   */
  openDrawer?: (row: Record<string, any>) => void
  /**
   * Variant/fpCode level gate. When false (e.g. platform variant or no
   * fpCode) the drawer is fully disabled. Per-row eligibility is then
   * a function of the row carrying the tuple identity fields.
   */
  drawerEligible?: boolean
  /**
   * Note count per row. Resolved by the host via
   * ``useExceptionNoteCounts`` + ``compositeKey``. Forwarded to
   * cell renderers via ``RenderContext.noteCount`` and used to
   * label the rowSpan=2 Details button on sales-compare tables.
   */
  getNoteCount?: (row: Record<string, any>) => number
  /**
   * Count of notes on the same object that live on a *different*
   * exception type. Forwarded to ``RenderContext.siblingNoteCount`` to
   * drive the "related exception" outline dot.
   */
  getSiblingNoteCount?: (row: Record<string, any>) => number
}

export default function TableBodyRows({
  displayedRows,
  visibleColumns,
  config,
  variant,
  fpCode,
  isInspector,
  expandedRows,
  onToggleExpand,
  rowClickEnabled,
  openRowDetail,
  hasExpandable,
  hasRowDetail,
  rowDetailEnabledFor,
  rowDetailType,
  rowDetailRowClickEnabled,
  isRowSplit,
  rowSplitSystems,
  totalColSpan,
  groupSeparatorKeys,
  getFreezeBodySx,
  tableRowDivider,
  rowHoverBg,
  isClosedView,
  activeRowKey,
  activeRowBg,
  openDrawer,
  drawerEligible,
  getNoteCount,
  getSiblingNoteCount,
}: TableBodyRowsProps) {
  // True while a note side-panel is open (the host derives
  // ``activeRowKey`` from ``panelRow``). Used to dim every non-active
  // row so the eye snaps straight to the source row - the active row
  // keeps full opacity + a stronger background tint.
  const isSpotlightMode = !!activeRowKey

  return (
    <TableBody>
      {displayedRows.map((row, idx) => {
        const rowKey = row[config.rowKeyField] ?? idx
        const rowKeyStr = String(rowKey)
        const rowHighlight = row._row_highlight as string | undefined
        const isExpanded = expandedRows[rowKeyStr]
        const expandBorder = hasExpandable && isExpanded ? 'none' : undefined

        // Per-row eligibility: drawer needs the tuple identity on the
        // row to anchor notes + the lifecycle context. Computed
        // per row so a missing/empty field on one row doesn't kill
        // the drawer trigger for siblings.
        // Object-anchored: the drawer needs only (data_domain, object_id) to
        // open in object-history mode. exception_code is optional - a
        // zero-issue browse row (no current exception) still anchors the
        // drawer on its object so historical lifecycle + comments are one
        // click away. Exception / tool rows always carry a code, so dropping
        // that requirement here only newly-enables the browse zero-issue rows.
        const rowHasTuple =
          typeof row?.data_domain === 'string'
          && row?.object_id !== undefined
          && row?.object_id !== null
          && String(row.object_id) !== ''
        const canOpenDrawer = !!drawerEligible && !!openDrawer && rowHasTuple
        const boundOpenDrawer = canOpenDrawer
          ? () => openDrawer!(row)
          : undefined

        // Whole-row click priority: expand toggle > rowDetail dialog
        // (duplicate-group configs with rowClick) > drawer. Split tables
        // (sales-compare) participate too - both stacked halves share the
        // same handler, and ``INTERACTIVE_TARGET_SELECTOR`` below still
        // protects link / button / chip cells, so clicking the Compare
        // button or a sale link keeps its own behaviour instead of
        // opening the drawer/dialog.
        const rowDetailClickEligible =
          !!rowDetailRowClickEnabled
          && (!rowDetailEnabledFor || rowDetailEnabledFor(row))
        const drawerRowClickEnabled =
          canOpenDrawer && !rowClickEnabled && !rowDetailClickEligible
        const handleRowClick =
          rowClickEnabled || rowDetailClickEligible || drawerRowClickEnabled
            ? (e: React.MouseEvent<HTMLTableRowElement>) => {
                const target = e.target as HTMLElement
                if (target.closest(INTERACTIVE_TARGET_SELECTOR)) return
                if (rowClickEnabled) onToggleExpand(rowKeyStr)
                else if (rowDetailClickEligible) openRowDetail(row)
                else if (drawerRowClickEnabled) openDrawer!(row)
              }
            : undefined
        const rowCursor =
          rowClickEnabled || rowDetailClickEligible || drawerRowClickEnabled
            ? 'pointer'
            : undefined

        const isActiveRow = !!activeRowKey && activeRowKey === rowKeyStr
        const configRowBg = config.getRowBgColor?.(row)
        // Priority (highest wins): closed-view mode tint > active-row
        // note-panel tint > config row-bg > _row_highlight palette >
        // zebra alternation. Closed-view dominates because it's a
        // mode-level state; everything else is per-row.
        const resolveRowBaseBg = (): string => {
          if (isClosedView) return CLOSED_ROW_BG
          if (isActiveRow && activeRowBg) return activeRowBg
          if (configRowBg) return configRowBg
          if (rowHighlight) return ROW_HIGHLIGHT_COLORS[rowHighlight]
          if (isInspector) return idx % 2 === 0 ? '#FFFFFF' : ZEBRA_BG
          return '#FFFFFF'
        }
        const rowBaseBg = resolveRowBaseBg()
        const closedRowSx = isClosedView ? { opacity: CLOSED_ROW_OPACITY } : null
        // Spotlight: dim every non-active row while a note panel is
        // open. Active row keeps full opacity AND gets a teal
        // left-border accent (same mechanism as blocked-row red
        // accent, distinct hue so the two states don't visually
        // collide on a blocked row whose panel is also open).
        const isDimmedRow = isSpotlightMode && !isActiveRow && !isClosedView
        const dimmedRowSx = isDimmedRow
          ? {
              opacity: INACTIVE_ROW_OPACITY,
              transition: 'opacity 120ms ease-out',
            }
          : null
        const activeRowAccentSx = isActiveRow && !isClosedView
          ? {
              '& > td:first-of-type': {
                boxShadow: `inset 4px 0 0 ${ACTIVE_ROW_ACCENT}`,
              },
            }
          : null
        // While a row is "active" (drawer open against it), hover is
        // suppressed entirely - the spotlight tint stands on its own
        // and a hover flip on top muddies the selected-row read.
        // Non-active rows keep the regular hover bg.
        const effectiveRowHoverBg =
          isActiveRow && !isClosedView ? rowBaseBg : rowHoverBg
        // Blocked rows are still open - they get a left-border accent
        // (not the strong background tint reserved for the Closed
        // view). The border is added to the first cell only via a
        // child selector below to keep the visual subtle but
        // unmissable.
        const isBlockedRow =
          row.exception_status === 'blocked' && !isClosedView
        const blockedRowSx = isBlockedRow && !isActiveRow
          ? {
              '& > td:first-of-type': {
                boxShadow: 'inset 3px 0 0 #DC2626',
              },
            }
          : null

        // Build cell node for a single (column, system) tuple. When
        // splitRender is set and a system is supplied, prefer it.
        // Otherwise fall back to render() (mirroring the original
        // single-row contract) and then to formatCellValue.
        const renderCellNode = (
          col: ExceptionColumnDef,
          system: 'lightspeed' | 'xero' | null,
        ): React.ReactNode => {
          if (system && col.splitRender) {
            return col.splitRender(row, system)
          }
          const ctx: RenderContext = {
            variant,
            fpCode,
            row,
            noteCount: getNoteCount?.(row),
            siblingNoteCount: getSiblingNoteCount?.(row),
            openDrawer: boundOpenDrawer,
          }
          const rendered = col.render
            ? col.render(row[col.key], row, ctx)
            : undefined
          if (rendered !== undefined) return rendered
          return formatCellValue(row[col.key], col as ColumnDef, row)
        }

        const cellSxFor = (
          col: ExceptionColumnDef,
          rowSpan?: number,
          system?: 'lightspeed' | 'xero',
        ): Record<string, any> => {
          // Merged cells (rowSpan=2 - Issues, Compare) opt out of the
          // row-scoped --row-bg variable so the row hover tint doesn't
          // shift them. They keep a fixed bg sourced from rowBaseBg
          // directly. They also need an explicit bottom border so the
          // inter-sale divider closes around them (the LS row's
          // `& td { borderBottom }` rule sits in a different DOM row).
          const isMerged = !!rowSpan && rowSpan > 1
          // LS row centres vertically - its values are the source of
          // truth and never carry a delta below. Xero row is top-aligned
          // so deltas hang under the value without pushing the value off
          // the LS-row line. Merged cells centre across both rows.
          const verticalAlign =
            isMerged || system === 'lightspeed'
              ? 'middle'
              : system === 'xero'
                ? 'top'
                : undefined
          return {
            ...getColumnStyle(col as ColumnDef, row[col.key], rowHighlight, row),
            ...(col.getCellStyle ? col.getCellStyle(row[col.key], row) : {}),
            ...(!isInspector && col.key === 'last_sync_at'
              ? { borderRight: '2px solid #E0E0E0' }
              : {}),
            ...(groupSeparatorKeys.has(col.key)
              ? { borderRight: GROUP_SEPARATOR_BORDER }
              : {}),
            ...getFreezeBodySx(col),
            ...(verticalAlign ? { verticalAlign } : {}),
            ...(isMerged
              ? {
                  backgroundColor: rowBaseBg,
                  borderBottom: `1px solid ${tableRowDivider}`,
                }
              : {}),
          }
        }

        const mergedCellExtras = isRowSplit
          ? {
              backgroundColor: rowBaseBg,
              borderBottom: `1px solid ${tableRowDivider}`,
            }
          : {}

        const expandCell = hasExpandable ? (
          <TableCell
            sx={{
              padding: '4px 8px',
              verticalAlign: 'middle',
              ...mergedCellExtras,
            }}
            rowSpan={isRowSplit ? 2 : undefined}
          >
            <IconButton
              size="small"
              onClick={() => onToggleExpand(rowKeyStr)}
              aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </TableCell>
        ) : null

        // rowDetail cell hosts the dialog-opening button only (rowSpan=2 on
        // split sales-compare tables). The drawer is reachable from the
        // right-frozen Issues chip, so the cell needs no extra affordance.
        // Label + icon follow the dialog type: LS<->Xero compare keeps the
        // Balance glyph; duplicate groups get the Difference glyph. Rows
        // the per-row gate rejects keep the cell empty so the column stays
        // aligned.
        const rowDetailEnabled =
          !rowDetailEnabledFor || rowDetailEnabledFor(row)
        const isDuplicateGroupDetail = rowDetailType === 'duplicate-group'
        const compareCell = hasRowDetail ? (
          <TableCell
            sx={{
              padding: '4px 8px',
              textAlign: 'center',
              verticalAlign: 'middle',
              ...mergedCellExtras,
            }}
            rowSpan={isRowSplit ? 2 : undefined}
          >
            {rowDetailEnabled ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  isDuplicateGroupDetail ? (
                    <DifferenceIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <BalanceIcon sx={{ fontSize: 16 }} />
                  )
                }
                onClick={(e) => {
                  e.stopPropagation()
                  openRowDetail(row)
                }}
                sx={{
                  minWidth: 0,
                  py: 0.125,
                  px: 1,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.4,
                }}
              >
                {isDuplicateGroupDetail ? 'Compare Duplicates' : 'Compare'}
              </Button>
            ) : null}
          </TableCell>
        ) : null

        const expandableDetail =
          hasExpandable && config.expandable ? (
            <TableRow key={`${rowKeyStr}-detail`}>
              <TableCell
                colSpan={totalColSpan}
                sx={{
                  p: 0,
                  borderBottom: isExpanded
                    ? `1px solid ${tableRowDivider}`
                    : 'none',
                }}
              >
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  {config.expandable.renderContent(row, { variant, fpCode, row })}
                </Collapse>
              </TableCell>
            </TableRow>
          ) : null

        if (isRowSplit && rowSplitSystems) {
          const [firstSystem, secondSystem] = rowSplitSystems
          // First (Lightspeed) row - emit expand cell, all spanned and
          // per-system cells, plus the rowDetail Compare cell.
          const firstRowCells = visibleColumns.map((col) => {
            if (col.spansSystems) {
              return (
                <TableCell key={col.key} rowSpan={2} sx={cellSxFor(col, 2)}>
                  {renderCellNode(col, null)}
                </TableCell>
              )
            }
            return (
              <TableCell key={col.key} sx={cellSxFor(col, undefined, firstSystem)}>
                {renderCellNode(col, firstSystem)}
              </TableCell>
            )
          })
          // Second (Xero) row - only non-spanned cells.
          const secondRowCells = visibleColumns
            .filter((col) => !col.spansSystems)
            .map((col) => (
              <TableCell key={col.key} sx={cellSxFor(col, undefined, secondSystem)}>
                {renderCellNode(col, secondSystem)}
              </TableCell>
            ))

          return (
            <React.Fragment key={rowKey}>
              <TableRow
                hover
                onClick={handleRowClick}
                data-row-key={rowKeyStr}
                sx={{
                  cursor: rowCursor,
                  '--row-bg': rowBaseBg,
                  backgroundColor: 'var(--row-bg)',
                  '&:hover': {
                    '--row-bg': effectiveRowHoverBg,
                    backgroundColor: 'var(--row-bg)',
                  },
                  // Light dashed divider between the LS half (this row)
                  // and the Xero half (next row) - keeps the pair
                  // visually grouped without obscuring the heavier
                  // inter-sale border below. Skip rowSpan cells (Issues,
                  // Compare, Expand) so they keep the inter-sale solid
                  // border set on the cell.
                  '& td:not([rowspan])': {
                    borderBottom: '1px dashed rgba(0,0,0,0.06)',
                  },
                  ...(closedRowSx ?? {}),
                  ...(blockedRowSx ?? {}),
                  ...(dimmedRowSx ?? {}),
                  ...(activeRowAccentSx ?? {}),
                }}
              >
                {expandCell}
                {firstRowCells}
                {compareCell}
              </TableRow>
              {/* Second (Xero) half of the split pair. ``activeRowAccentSx``
                  and ``blockedRowSx`` are intentionally omitted here -
                  their ``& > td:first-of-type`` accent on the first
                  half's leftmost ``rowSpan={2}`` cell already paints
                  the full-height left-edge bar across both halves;
                  applying them here too would land a second, stray bar
                  on the Xero row's first non-spanning cell (the date
                  column). ``closedRowSx`` and ``dimmedRowSx`` are
                  whole-row opacity and stay on both halves so the pair
                  fades together. */}
              <TableRow
                hover
                onClick={handleRowClick}
                sx={{
                  cursor: rowCursor,
                  '--row-bg': rowBaseBg,
                  backgroundColor: 'var(--row-bg)',
                  '&:hover': {
                    '--row-bg': effectiveRowHoverBg,
                    backgroundColor: 'var(--row-bg)',
                  },
                  '& td': {
                    borderBottom: expandBorder || `1px solid ${tableRowDivider}`,
                  },
                  ...(closedRowSx ?? {}),
                  ...(dimmedRowSx ?? {}),
                }}
              >
                {secondRowCells}
              </TableRow>
              {expandableDetail}
            </React.Fragment>
          )
        }

        return (
          <React.Fragment key={rowKey}>
            <TableRow
              hover
              onClick={handleRowClick}
              data-row-key={rowKeyStr}
              sx={{
                cursor: rowCursor,
                '--row-bg': rowBaseBg,
                backgroundColor: 'var(--row-bg)',
                '&:hover': {
                  '--row-bg': effectiveRowHoverBg,
                  backgroundColor: 'var(--row-bg)',
                },
                '& td': {
                  borderBottom: expandBorder || `1px solid ${tableRowDivider}`,
                },
                ...(closedRowSx ?? {}),
                ...(dimmedRowSx ?? {}),
                ...(activeRowAccentSx ?? {}),
              }}
            >
              {expandCell}
              {visibleColumns.map((col) => (
                <TableCell key={col.key} sx={cellSxFor(col)}>
                  {renderCellNode(col, null)}
                </TableCell>
              ))}
              {compareCell}
            </TableRow>
            {expandableDetail}
          </React.Fragment>
        )
      })}
    </TableBody>
  )
}
